import { useEffect, useState } from 'react';

/**
 * usePWAInstall - manages PWA install prompt + manifest swap for the portal.
 * Returns { installable, install, isInstalled }
 */
export default function usePWAInstall(manifestHref = '/portal-manifest.json', themeColor = '#0F172A') {
  const [installable, setInstallable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // Swap the page manifest + theme color for the portal experience
  useEffect(() => {
    const original = document.querySelector('link[rel="manifest"]');
    const originalHref = original?.getAttribute('href');
    const originalTheme = document.querySelector('meta[name="theme-color"]')?.getAttribute('content');

    if (original) original.setAttribute('href', manifestHref);
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      document.head.appendChild(themeMeta);
    }
    themeMeta.setAttribute('content', themeColor);

    return () => {
      if (original && originalHref) original.setAttribute('href', originalHref);
      if (themeMeta && originalTheme) themeMeta.setAttribute('content', originalTheme);
    };
  }, [manifestHref, themeColor]);

  // Detect installable + currently installed
  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setInstallable(true);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setInstallable(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // Already installed (running as standalone)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setInstallable(false);
    return choice.outcome === 'accepted';
  };

  return { installable, install, isInstalled };
}
